import { Aide } from '@/components/ui/aide';
import React from 'react';
import { Link } from 'react-router-dom';
import { Fuel } from 'lucide-react';

import { formatDate } from '../../lib/formats';
import { libelleCamion } from '../../lib/stockTransit';
import { formatCompactVolume } from '../../lib/utils';

const SOURCES_LIVRAISON = ['AUTO_TRIP_DIRECT_DELIVERY', 'AUTO_DISPATCH'];
const TYPES_SORTIE = ['SALE', 'INTERNAL_TRANSFER'];

/**
 * Les dernières sorties de stock qui sont des livraisons (vente directe ou
 * livraison de commande), de la plus récente à la plus ancienne. Les données
 * sont celles que l'écran Stock a déjà chargées: aucune requête de plus.
 */
export const livraisonsRecentes = ({ mouvements, trajets, camions, commandes, clients, max = 4 }) => {
  const trajetParId = new Map((trajets || []).filter(Boolean).map((t) => [t.id, t]));
  const commandeParId = new Map((commandes || []).filter(Boolean).map((c) => [c.id, c]));
  const clientParId = new Map((clients || []).filter(Boolean).map((c) => [c.id, c]));
  return (mouvements || [])
    .filter((m) => m
      // Une livraison est une vente rattachée à un trajet ou à une commande,
      // quelle que soit la façon dont elle a été saisie (automatique, à la
      // main, reprise d'historique).
      && (SOURCES_LIVRAISON.includes(m.source)
        || (m.movement_type === 'SALE' && (m.trip_id || m.order_id)))
      && TYPES_SORTIE.includes(m.movement_type)
      && Number(m.quantity_liters) > 0)
    .sort((a, b) => String(b.movement_date || '').localeCompare(String(a.movement_date || '')))
    .slice(0, max)
    .map((m) => {
      const trajet = trajetParId.get(m.trip_id);
      const commande = commandeParId.get(m.order_id) || commandeParId.get(trajet?.order_id);
      const clientId = commande?.client_id || trajet?.client_id;
      return {
        id: m.id,
        tripId: m.trip_id || null,
        plaque: libelleCamion({ truckId: m.truck_id, trajet, camions }),
        client: clientParId.get(clientId)?.name || trajet?.client_name || null,
        litres: Number(m.quantity_liters) || 0,
        date: formatDate(m.movement_date, 'Date non précisée'),
      };
    });
};

export const DernieresLivraisons = ({ livraisons }) => {
  if (!livraisons.length) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-5 text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-muted text-muted-foreground">
          <Fuel className="h-7 w-7" aria-hidden="true" />
        </span>
        <div className="flex items-center gap-1">
          <p className="text-sm font-semibold text-foreground">Aucune livraison enregistrée</p>
          <Aide titre="Aucune livraison enregistrée">Les ventes directes et les livraisons de commande apparaîtront ici dès qu’un camion aura déchargé.</Aide>
        </div>
      </div>
    );
  }
  return (
    <ul className="space-y-3" data-testid="dernieres-livraisons">
      {livraisons.map((l) => {
        const contenu = (
          <>
            <div className="flex items-baseline justify-between gap-3">
              <p className="min-w-0 truncate text-sm font-bold text-foreground">{l.plaque}</p>
              <p className="shrink-0 text-sm font-bold tabular-nums text-foreground">{formatCompactVolume(l.litres)}</p>
            </div>
            <p className="truncate text-xs text-muted-foreground">
              {[l.client, l.date].filter(Boolean).join(' · ')}
            </p>
          </>
        );
        const classes = 'block rounded-xl border border-border/70 bg-background px-3 py-2.5';
        return (
          <li key={l.id}>
            {l.tripId ? (
              <Link to={`/trips/${l.tripId}`} className={`${classes} transition-colors hover:bg-muted/50`}>
                {contenu}
              </Link>
            ) : (
              <div className={classes}>{contenu}</div>
            )}
          </li>
        );
      })}
    </ul>
  );
};
